import { ApiClient } from './apiClient';

// ===============================
// TIPOS PARA ASIGNACIONES USUARIO-OPERADOR
// ===============================

export interface OperadorParaDragDrop {
  id: number;
  codigo: string;
  descripcion: string;
  nombre_completo: string | null;
  rut: string | null;
  telefono: string | null;
  email: string | null;
  estado: string;
}

export interface UsuarioConOperadores {
  usuario_uau_id: number;
  operadores: OperadorParaDragDrop[];
}

export interface RespuestaDatosDragDropOperadores {
  usuarios_con_operadores: UsuarioConOperadores[];
  operadores_disponibles: OperadorParaDragDrop[];
  organizacion_id: number;
  total_usuarios: number;
  total_operadores: number;
  operadores_asignados: number;
}

export interface ElementoAsignacionOperador {
  usuario_uau_id: number;
  operador_ids: number[];
}

export interface SolicitudAsignacionLoteOperadores {
  asignaciones: ElementoAsignacionOperador[];
  notas?: string;
}

export interface RespuestaAsignacionLoteOperadores {
  exitoso: boolean;
  mensaje: string;
  estadisticas: {
    creadas: number;
    actualizadas: number;
    errores: number;
  };
  errores: string[];
}

export interface ResumenAsignacionesOperadores {
  total_operadores: number;
  operadores_asignados: number;
  operadores_disponibles: number;
  usuarios_con_asignaciones: number;
  porcentaje_asignacion: number;
}

export interface CrearAsignacionUsuarioOperador {
  usuario_uau_id: number;
  operador_id: number;
  notas?: string;
}

export interface VerificacionAccesoOperador {
  usuario_uau_id: number;
  operador_id: number;
  tiene_acceso: boolean;
  asignacion_id?: number;
  es_sin_restricciones: boolean;
}

// ===============================
// SERVICIO DE ASIGNACIONES USUARIO-OPERADOR
// ===============================

export class AsignacionesUsuarioOperadorService {
  private static readonly BASE_PATH = '/admin/asignaciones-usuario-operador';

  // ===============================
  // ENDPOINTS PARA DRAG & DROP
  // ===============================

  /**
   * Obtener datos completos para la interfaz drag & drop
   * Solo accesible para superusers UAU
   */
  static async obtenerDatosDragDrop(): Promise<RespuestaDatosDragDropOperadores> {
    return ApiClient.get<RespuestaDatosDragDropOperadores>(`${this.BASE_PATH}/usuarios-operadores`);
  }

  /**
   * Obtener datos para un usuario específico (operadores disponibles filtrados)
   * Solo accesible para superusers UAU
   */
  static async obtenerDatosDragDropParaUsuario(usuarioId: number): Promise<RespuestaDatosDragDropOperadores> {
    return ApiClient.get<RespuestaDatosDragDropOperadores>(`${this.BASE_PATH}/usuarios-operadores?usuario_seleccionado=${usuarioId}`);
  }

  /**
   * Guardar cambios de drag & drop mediante asignación batch
   * Solo accesible para superusers UAU
   */
  static async asignarOperadoresLote(solicitud: SolicitudAsignacionLoteOperadores): Promise<RespuestaAsignacionLoteOperadores> {
    return ApiClient.post<any>(`${this.BASE_PATH}/asignaciones/lote`, solicitud) as unknown as Promise<RespuestaAsignacionLoteOperadores>;
  }

  /**
   * Obtener operadores disponibles para asignación
   * Solo accesible para superusers UAU
   */
  static async obtenerOperadoresDisponibles(excluirUsuarioId?: number): Promise<OperadorParaDragDrop[]> {
    const params = excluirUsuarioId ? `?excluir_usuario_id=${excluirUsuarioId}` : '';
    return ApiClient.get<OperadorParaDragDrop[]>(`${this.BASE_PATH}/operadores-disponibles${params}`);
  }

  // ===============================
  // ENDPOINTS INDIVIDUALES
  // ===============================

  /**
   * Crear asignación individual usuario-operador
   * Solo accesible para superusers UAU
   */
  static async crearAsignacion(datos: CrearAsignacionUsuarioOperador): Promise<any> {
    const response = await ApiClient.post(`${this.BASE_PATH}/asignaciones`, datos);
    return response.data;
  }

  /**
   * Eliminar asignación individual usuario-operador
   * Solo accesible para superusers UAU
   */
  static async eliminarAsignacion(usuarioUauId: number, operadorId: number): Promise<void> {
    await ApiClient.delete(`${this.BASE_PATH}/asignaciones/${usuarioUauId}/operadores/${operadorId}`);
  }

  // ===============================
  // ENDPOINTS DE ESTADÍSTICAS
  // ===============================

  /**
   * Obtener resumen de asignaciones para dashboard administrativo
   * Solo accesible para superusers UAU
   */
  static async obtenerResumenAsignaciones(): Promise<ResumenAsignacionesOperadores> {
    return ApiClient.get<ResumenAsignacionesOperadores>(`${this.BASE_PATH}/asignaciones/resumen`);
  }

  // ===============================
  // ENDPOINTS PARA USUARIO ACTUAL
  // ===============================

  /**
   * Obtener operadores asignados al usuario actual para filtros de reportes
   * Accesible para cualquier usuario autenticado
   */
  static async obtenerMisOperadoresAsignados(): Promise<OperadorParaDragDrop[]> {
    return ApiClient.get<OperadorParaDragDrop[]>('/reportes/mis-operadores');  // ✅ ENDPOINT CORRECTO
  }

  /**
   * Verificar si el usuario actual tiene acceso a un operador específico
   * Usado para filtrado automático en otros módulos
   */
  static async verificarAccesoOperador(operadorId: number): Promise<VerificacionAccesoOperador> {
    return ApiClient.get<VerificacionAccesoOperador>(`${this.BASE_PATH}/verificar-acceso/${operadorId}`);
  }

  // ===============================
  // UTILIDADES Y HELPERS
  // ===============================

  /**
   * Convertir datos del drag & drop al formato de solicitud batch
   */
  static convertirDragDropALote(usuariosConOperadores: UsuarioConOperadores[], notas?: string): SolicitudAsignacionLoteOperadores {
    const asignaciones: ElementoAsignacionOperador[] = usuariosConOperadores.map(usuario => ({
      usuario_uau_id: usuario.usuario_uau_id,
      operador_ids: usuario.operadores.map(o => o.id)
    }));

    return {
      asignaciones,
      notas
    };
  }

  /**
   * Obtener estadísticas básicas de las asignaciones
   */
  static calcularEstadisticas(datos: RespuestaDatosDragDropOperadores): {
    usuariosConOperadores: number;
    operadoresSinAsignar: number;
    totalAsignaciones: number;
    porcentajeCobertura: number;
  } {
    const usuariosConOperadores = datos.usuarios_con_operadores.length;
    const operadoresSinAsignar = datos.operadores_disponibles.length;
    const totalAsignaciones = datos.usuarios_con_operadores.reduce(
      (total, usuario) => total + usuario.operadores.length, 
      0
    );
    const porcentajeCobertura = datos.total_operadores > 0 
      ? Math.round((datos.operadores_asignados / datos.total_operadores) * 100)
      : 0;

    return {
      usuariosConOperadores,
      operadoresSinAsignar,
      totalAsignaciones,
      porcentajeCobertura
    };
  }

  /**
   * Validar que las asignaciones son correctas antes del envío
   */
  static validarAsignaciones(solicitud: SolicitudAsignacionLoteOperadores): {
    esValida: boolean;
    errores: string[];
    advertencias: string[];
  } {
    const errores: string[] = [];
    const advertencias: string[] = [];

    // Verificar que hay asignaciones
    if (!solicitud.asignaciones || solicitud.asignaciones.length === 0) {
      advertencias.push('No hay asignaciones para procesar');
    }

    // Verificar duplicados de operadores
    const operadoresAsignados = new Set<number>();
    const operadoresDuplicados = new Set<number>();

    for (const asignacion of solicitud.asignaciones) {
      if (!asignacion.usuario_uau_id) {
        errores.push('Todas las asignaciones deben tener un usuario válido');
        continue;
      }

      for (const operadorId of asignacion.operador_ids) {
        if (operadoresAsignados.has(operadorId)) {
          operadoresDuplicados.add(operadorId);
        } else {
          operadoresAsignados.add(operadorId);
        }
      }
    }

    if (operadoresDuplicados.size > 0) {
      errores.push(`Operadores asignados a múltiples usuarios: ${Array.from(operadoresDuplicados).join(', ')}`);
    }

    return {
      esValida: errores.length === 0,
      errores,
      advertencias
    };
  }

  // ===============================
  // HOOKS PARA REACT QUERY (OPCIONALES)
  // ===============================

  /**
   * Query keys para React Query
   */
  static readonly queryKeys = {
    all: ['asignaciones-usuario-operador'] as const,
    dragDrop: () => [...this.queryKeys.all, 'drag-drop'] as const,
    misOperadores: () => [...this.queryKeys.all, 'mis-operadores'] as const,
    resumen: () => [...this.queryKeys.all, 'resumen'] as const,
    operadoresDisponibles: () => [...this.queryKeys.all, 'disponibles'] as const,
    accesoOperador: (operadorId: number) => [...this.queryKeys.all, 'acceso', operadorId] as const,
  };

  /**
   * Mutación keys para React Query
   */
  static readonly mutationKeys = {
    asignarLote: 'asignar-lote-operadores' as const,
    crearAsignacion: 'crear-asignacion-operador' as const,
    eliminarAsignacion: 'eliminar-asignacion-operador' as const,
  };
}
